import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";
import { isGlobalViewsWebsite, isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";

const enum fields {
    title = "title",
    desc = "desc",
    link = "link",
    link2 = "link2",
    label = "label",
    label2 = "label2",
    cssClass = "cssClass",
    openInNewWidow = "openInNewWidow",
    emailInfo = "emailInfo",
    backgroundColor = "backgroundColor",
    borderColor = "borderColor",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.desc]: string;
        [fields.link]: LinkFieldValue;
        [fields.link2]: LinkFieldValue;
        [fields.label]: string;
        [fields.label2]: string;
        [fields.emailInfo]: string;
        [fields.openInNewWidow]: boolean;
        [fields.backgroundColor]: string;
        [fields.borderColor]: string;
        [fields.textColor]: string;
    };
}

type Props = OwnProps;

export interface SurFaqFooterInfoStyles {
    container?: InjectableCss;
    ctacontainer?: InjectableCss;
    title?: InjectableCss;
    descStyles?: InjectableCss;
    ctaLink?: LinkProps;
    emailInfo?: InjectableCss;
    buttonContainer?: InjectableCss;
    text?: InjectableCss;
}
export const surFaqFooterInfoStyles: SurFaqFooterInfoStyles = {
    container: {
        css: css`
            padding: 60px 0px 60px 0px;
            max-width: 955px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            row-gap: 20px;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
            p, span {
                font-family: inherit !important;
                color: inherit !important;
            }
        `,
    },
    descStyles: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
            p, span {
                font-family: inherit !important;
                color: inherit !important;
            }
        `,
    },
    ctacontainer: {
        css: css`
            text-align: center;
            margin-top: 30px;
            padding: 10px 34px;
            background: ${getColor("primary")};
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            max-width: 622px;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    text: {
        css: css`
            padding: 0px 20px;
            white-space: nowrap;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px 0px;
                    `,
                    css`
                        padding: 10px 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = surFaqFooterInfoStyles;

const SurFaqFooterInfoContent: React.FC<Props> = ({ fields, id }) => {
    const { url } = useGetLink(fields.link);
    const isMgbw = isMgbwWebsite();
    const secondaryBtn = (ELEMENTS.button as any).secondary;

    const ctaLink = {
        css: css`
            outline: 0;
            background-color: ${isMgbw ? secondaryBtn.background : fields.backgroundColor};
            border: ${isMgbw ? secondaryBtn.borderWidth : "1px"} solid
                ${isMgbw ? secondaryBtn.borderColor : fields.borderColor};
            padding: ${isMgbw ? ELEMENTS.button.base.padding : "10px 34px"};
            max-width: 100%;
            margin: 0 auto;

            ${isGlobalViewsWebsite() &&
                css`
                    border-radius: 4px;
                    &&, && * {
                        font-family: ${TYPOGRAPHY.fontFamily.base} !important;
                    }
                `}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    null,
                    null,
                ])}
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 24px;
                text-align: right;
                color: ${isMgbw ? `${secondaryBtn.color} !important` : `${getColor("text.main")} !important`};
                font-style: normal !important;

                ${isGlobalViewsWebsite() &&
                    css`
                        letter-spacing: 0.2px;
                    `}

                p {
                    margin-bottom: 0px;
                }
            }
            ${isMgbw
                ? css`
                      &:hover {
                          background-color: ${secondaryBtn.hover.background};
                          border-color: ${secondaryBtn.hover.borderColor};
                          span {
                              color: ${secondaryBtn.hover.color} !important;
                          }
                      }
                  `
                : ""}
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            {fields.title && <StyledWrapper {...styles.title}>{parse(fields.title, parserOptions)}</StyledWrapper>}
            {fields.desc && <StyledWrapper {...styles.descStyles}>{parse(fields.desc, parserOptions)}</StyledWrapper>}

            {fields.emailInfo && (
                <StyledWrapper {...styles.emailInfo}>{parse(fields.emailInfo, parserOptions)}</StyledWrapper>
            )}
            {fields.label && (
                <StyledWrapper {...styles.buttonContainer}>
                    {fields.label ? (
                        <Link {...ctaLink} aria-label="Email Us">
                            {parse(fields.label, parserOptions)}
                        </Link>
                    ) : null}
                    <StyledWrapper {...styles.text}>OR</StyledWrapper>
                    {fields.label2 ? (
                        <Link {...ctaLink} aria-label="Call Us">
                            {parse(fields.label2, parserOptions)}
                        </Link>
                    ) : null}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurFaqFooterInfoContent,
    definition: {
        group: "Common",
        displayName: "SurFaqFooterInfoContent",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desc,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.label2,
                fieldType: "General",
                displayName: "Button Label 2",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },

            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "Translatable",
                name: fields.emailInfo,
                editorTemplate: "RichTextField",
                displayName: "Email Info",
                defaultValue: "",
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
